import PropTypes from 'prop-types';
import { useContext } from 'react';
import EverTrue from 'app';
import { useFeatureFlag } from 'entities/helpers/use-feature-flag';
import { TasksNotificationContext } from '../hooks/tasks-notification-context';
import DotNotificationBadge from 'components/notifications/dot-notification-badge.js';
import NavigationExpandableItem from 'apps/layout/components/navigation-expandable-item';
import NavigationNestedItem from 'apps/layout/components/navigation-nested-item';
import NavigationSubItem from 'apps/layout/components/navigation-sub-item';
import NewBadge from 'components/elements/new-badge';
import { trackTaskAction } from 'apps/tasks/utils/utils';

/**
 * Tasks navigation link with feature-flag-driven rendering:
 * - Signal only: simple "Tasks" link to /tasks
 * - Cadence V2: expandable section with Task Summary, Manual Tasks, and Automated Tasks
 * - Neither flag: hidden
 */
const NavigationTasksExpandableLink = ({ active_path = '', isCollapsed }) => {
  const { enabled, loaded, tasksDueCount = {} } = useContext(TasksNotificationContext);
  const { flagEnabled: cadenceV2Enabled, loaded: cadenceV2Loaded } = useFeatureFlag('cadenceV2Internal');
  const { flagEnabled: signalEnabled, loaded: signalLoaded } = useFeatureFlag('signal');
  const { owned = 0 } = tasksDueCount;

  if (!loaded || !cadenceV2Loaded || !signalLoaded) return null;
  if (!enabled) return null;
  if (!signalEnabled && !cadenceV2Enabled) return null;

  const tasksRegex = /^\/tasks.*/;

  // Signal only (no cadence v2) — render a simple Tasks link
  if (signalEnabled && !cadenceV2Enabled) {
    const isActive = !!active_path.match(tasksRegex);
    return (
      <NavigationSubItem
        icon="tasks"
        iconKit="gt2"
        label="Tasks"
        isActive={isActive}
        onClick={() => {
          trackTaskAction('navigate_to_tasks_from_left_nav');
          EverTrue.Navigator('/tasks', true);
        }}
      >
        <DotNotificationBadge
          popoverText={'Incomplete Tasks Due Today'}
          showBadge={!!owned}
          style={{ marginLeft: isCollapsed ? '5px' : '8px' }}
        />
      </NavigationSubItem>
    );
  }

  // Cadence V2 enabled — render expandable nav with sub-items
  const tasksSummaryRegex = /^\/tasks\/summary.*/;
  const manualTasksRegex = /^\/tasks\/manual.*/;
  const automatedTasksRegex = /^\/tasks\/automated.*/;

  const isParentActive = !!active_path.match(tasksRegex);
  const isSummaryActive = !!active_path.match(tasksSummaryRegex);
  const isManualActive =
    !!active_path.match(manualTasksRegex) ||
    (active_path === '/tasks' && !active_path.match(automatedTasksRegex) && !active_path.match(tasksSummaryRegex));
  const isAutomatedActive = !!active_path.match(automatedTasksRegex);

  return (
    <NavigationExpandableItem
      icon="tasks"
      iconKit="gt2"
      label="Tasks"
      isActive={isParentActive}
      defaultExpanded={isParentActive}
      badge={
        <DotNotificationBadge
          popoverText={'Incomplete Tasks Due Today'}
          showBadge={!!owned}
          style={{ marginLeft: isCollapsed ? '5px' : '8px' }}
        />
      }
    >
      <NavigationNestedItem
        label="Task Summary"
        path="/tasks/summary"
        isActive={isSummaryActive}
        trackingType="tasks_summary"
        badge={<NewBadge />}
        onClick={() => {
          trackTaskAction('navigate_to_tasks_summary_from_left_nav');
          EverTrue.Navigator('/tasks/summary', true);
        }}
      />
      <NavigationNestedItem
        label="Manual Tasks"
        path="/tasks"
        isActive={isManualActive}
        trackingType="manual_tasks"
        onClick={() => {
          trackTaskAction('navigate_to_manual_tasks_from_left_nav');
          EverTrue.Navigator('/tasks', true);
        }}
      />
      <NavigationNestedItem
        label="Automated Tasks"
        path="/tasks/automated"
        isActive={isAutomatedActive}
        trackingType="automated_tasks"
        badge={<NewBadge />}
        onClick={() => {
          trackTaskAction('navigate_to_automated_tasks_from_left_nav');
          EverTrue.Navigator('/tasks/automated', true);
        }}
      />
    </NavigationExpandableItem>
  );
};

NavigationTasksExpandableLink.propTypes = {
  active_path: PropTypes.string,
  isCollapsed: PropTypes.bool,
};

export default NavigationTasksExpandableLink;
